import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Plus } from 'lucide-react';
import { Amp } from '../../components/Amp';
import { Navbar } from '../../components/Navbar';
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from '../../components/SEO';

const FAQItem = ({ faq, index, isOpen, onToggle, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.04 }}
    className="border-b border-[#E2E8F0] last:border-b-0"
  >
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-6 py-6 text-left group"
      aria-expanded={isOpen}
    >
      <h3 className="text-[#0F172A] font-semibold text-base lg:text-lg leading-snug group-hover:text-[#374151] transition-colors">
        {faq.question}
      </h3>
      <span
        className="shrink-0 mt-1 w-7 h-7 flex items-center justify-center border transition-colors duration-300"
        style={{
          borderColor: isOpen ? accent : '#E2E8F0',
          backgroundColor: isOpen ? accent : 'transparent',
        }}
      >
        <Plus
          size={14}
          className="transition-transform duration-300"
          style={{
            color: isOpen ? '#fff' : '#9CA3AF',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <p className="text-[#374151] text-sm lg:text-base leading-relaxed pb-6 pr-13 max-w-2xl">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export const FAQSection = ({ faqs, theme, title }) => {
  const [openIndex, setOpenIndex] = useState(0);
  if (!faqs || faqs.length === 0) return null;

  return (
    <section className="section-padding bg-white border-t border-[#E2E8F0]">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label" style={{ color: theme.primary }}>Frequently Asked</span>
              <span className="w-6 h-px" style={{ backgroundColor: `${theme.primary}40` }} />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A] mb-5 leading-tight"
              style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}
            >
              {title}
            </h2>
            <p className="text-[#374151] text-sm leading-relaxed max-w-sm">
              The questions clients ask us most often. Don't see yours?{' '}
              <Link
                to="/book"
                className="font-medium underline decoration-1 underline-offset-4 transition-colors"
                style={{ color: theme.primary }}
              >
                Just ask
              </Link>
              .
            </p>
          </motion.div>

          <div className="lg:col-span-8">
            <div className="border-t border-[#E2E8F0]">
              {faqs.map((faq, i) => (
                <FAQItem
                  key={faq.question}
                  faq={faq}
                  index={i}
                  isOpen={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  accent={theme.primary}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PillarPage = ({
  meta,
  theme,
  services,
  process,
  cta,
  badgeRow,
  seo,
  faqs,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemas = seo
    ? [
        serviceSchema({
          name: seo.serviceName || `${meta.title} Services`,
          description: seo.description || meta.description,
          path: seo.path,
        }),
        ...(faqs && faqs.length ? [faqSchema(faqs)] : []),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/#services' },
          { name: meta.title, path: seo.path },
        ]),
      ]
    : [];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {seo && (
        <SEO
          title={seo.title || meta.title}
          description={seo.description || meta.description}
          path={seo.path}
          jsonLd={schemas}
        />
      )}
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-[#E2E8F0]">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[130px] pointer-events-none"
          style={{ backgroundColor: `${theme.accent}10` }}
        />
        <div className="container-custom py-20 lg:py-28 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label" style={{ color: theme.primary }}>{meta.label}</span>
              <span className="w-6 h-px" style={{ backgroundColor: `${theme.primary}40` }} />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-6 leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
            >
              {meta.headline.split(meta.italicWord).map((part, i, arr) => (
                <span key={i}>
                  <Amp>{part}</Amp>
                  {i < arr.length - 1 && (
                    <span className="italic" style={{ color: theme.primary }}>
                      <Amp>{meta.italicWord}</Amp>
                    </span>
                  )}
                </span>
              ))}
            </h1>
            <p className="text-[#374151] text-lg max-w-2xl leading-relaxed mb-8">
              {meta.description}
            </p>
            {(badgeRow || meta.badges) && (
              <div className="flex flex-wrap gap-3 mb-10">
                {badgeRow}
                {!badgeRow && meta.badges && meta.badges.map((b) => (
                  <span
                    key={b.label}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border text-xs font-mono tracking-wide"
                    style={{
                      borderColor: `${theme.primary}40`,
                      color: theme.primary,
                      backgroundColor: theme.soft,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.primary }} />
                    {b.label}
                  </span>
                ))}
              </div>
            )}
            <Link
              to="/book"
              className="group inline-flex items-center gap-2 px-7 py-3.5 text-white text-sm font-semibold transition-colors duration-300 shadow-sm"
              style={{ backgroundColor: theme.primary }}
            >
              Start Your Project
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-[#F8F7F4]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label" style={{ color: theme.primary }}>What We Offer</span>
              <span className="w-6 h-px" style={{ backgroundColor: `${theme.primary}40` }} />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
            >
              {meta.servicesHeading || 'Our Service Catalog'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0]">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="group bg-white p-8 transition-colors duration-300 relative overflow-hidden"
                  style={{ '--hover-bg': theme.soft }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.soft)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                    style={{ backgroundColor: theme.primary }}
                  />
                  <div
                    className="w-10 h-10 flex items-center justify-center mb-5 border"
                    style={{ borderColor: `${theme.primary}25`, backgroundColor: theme.soft }}
                  >
                    <Icon size={18} style={{ color: theme.primary }} />
                  </div>
                  <h3 className="text-[#0F172A] font-semibold mb-3 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#374151] text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-xs text-[#374151]">
                        <CheckCircle2 size={13} className="shrink-0 mt-0.5" style={{ color: theme.primary }} />
                        {h}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      {process && (
        <section className="section-padding bg-white border-t border-b border-[#E2E8F0]">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="section-label" style={{ color: theme.primary }}>How We Work</span>
                <span className="w-6 h-px" style={{ backgroundColor: `${theme.primary}40` }} />
              </div>
              <h2
                className="font-display font-bold text-[#0F172A]"
                style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
              >
                Our Delivery Process
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2E8F0]">
              {process.map((p, i) => (
                <motion.div
                  key={p.step}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="bg-white p-6 relative"
                >
                  <span className="font-mono text-[0.58rem] tracking-widest text-[#9CA3AF] block mb-3">{p.step}</span>
                  <h4 className="font-semibold text-[#0F172A] mb-2 text-sm">{p.label}</h4>
                  <p className="text-[#374151] text-xs leading-relaxed">{p.desc}</p>
                  {i < process.length - 1 && (
                    <span className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 text-[#9CA3AF] text-xs z-10">→</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <FAQSection faqs={faqs} theme={theme} title={`${meta.title} FAQs`} />

      {/* CTA */}
      <section className="section-padding" style={{ backgroundColor: cta.bg || '#2563EB' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2
              className="font-display font-bold text-white mb-6 leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
            >
              {cta.headline} <span className="italic" style={{ color: theme.accent }}>{cta.italic}</span>
            </h2>
            <Link
              to="/book"
              className="group inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold transition-all duration-300 shadow-lg"
              style={{ backgroundColor: theme.primary, color: '#fff' }}
            >
              {cta.buttonLabel || 'Book a Free Consultation'}
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] bg-white py-8">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#9CA3AF]">
          <span>© {new Date().getFullYear()} Smart Solutions. All rights reserved.</span>
          <Link to="/" className="hover:text-[#374151] transition-colors">Back to home →</Link>
        </div>
      </footer>
    </div>
  );
};
